/**
 * Default easing curves — The Easing Blueprint
 *
 * Source: https://animations.dev/learn/animation-theory/the-easing-blueprint
 *
 * The blueprint's thesis: pick one good easing curve and use it consistently
 * for the ~80% of animations that don't need anything special. Only reach for
 * the other two curves when the motion is specifically an exit or a position
 * change.
 *
 * | Curve                | When to use                                       |
 * |----------------------|---------------------------------------------------|
 * | `EASE_DEFAULT`       | Default — entrances, hover, user-initiated UI     |
 * | `EASE_EXIT`          | Exits only — content leaving the viewport         |
 * | `EASE_POSITION`      | Position changes — elements moving A → B          |
 *
 * Duration pairing (from the blueprint + Radix tradition used elsewhere):
 *   - entrance: 200ms
 *   - exit:     150ms
 *   - position: 300ms
 *
 * Two shapes are exported for every curve:
 *   - `*_CSS`    — string for CSS `transition-timing-function` / `animation`
 *   - `*_MOTION` — number tuple for Motion's `transition: { ease: [...] }`
 *
 * ## Usage examples
 *
 * ### Tailwind classes (most common)
 *
 * The semantic `--ease-*` CSS variables in `tokens.css` generate Tailwind
 * utilities with the same names:
 *
 * ```tsx
 * // Entrance / hover
 * <div className="transition-opacity duration-normal ease-default hover:opacity-100" />
 *
 * // Exit
 * <div className="transition-opacity duration-fast ease-exit" />
 *
 * // Position change
 * <div className="transition-transform duration-slow ease-position" />
 * ```
 *
 * ### Motion (`motion/react`)
 *
 * Use the pre-built transition objects — they pair the correct curve + duration
 * and handle the ms→s conversion:
 *
 * ```tsx
 * import { MOTION_ENTRANCE, MOTION_EXIT, MOTION_POSITION } from '@/lib/ui/animation';
 *
 * // Entrance
 * <motion.div
 *   initial={{ opacity: 0, y: 12 }}
 *   animate={{ opacity: 1, y: 0 }}
 *   transition={MOTION_ENTRANCE}
 * />
 *
 * // Exit (inside AnimatePresence)
 * <motion.div
 *   exit={{ opacity: 0, scale: 0.95 }}
 *   transition={MOTION_EXIT}
 * />
 *
 * // Position change (list reorder)
 * <motion.li layout transition={MOTION_POSITION} />
 * ```
 *
 * ### Inline CSS (rare — when Tailwind classes aren't enough)
 *
 * ```tsx
 * import { EASE_DEFAULT_CSS } from '@/lib/ui/easing';
 *
 * <div style={{ transition: `width 200ms ${EASE_DEFAULT_CSS}` }} />
 * ```
 *
 * ### CSS keyframes in `globals.css`
 *
 * ```css
 * .my-slide-in {
 *   animation: slide-in 200ms var(--ease-default);
 * }
 * ```
 */

/** The blueprint's recommended default — a smooth, versatile deceleration. */
export const EASE_DEFAULT_CSS = 'cubic-bezier(0.32, 0.72, 0, 1)' as const;
export const EASE_DEFAULT_MOTION: readonly [number, number, number, number] = [
  0.32, 0.72, 0, 1,
] as const;

/** For exits — elements leaving the viewport. Rarely needed; default first. */
export const EASE_EXIT_CSS = 'cubic-bezier(0.7, 0, 0.84, 0)' as const;
export const EASE_EXIT_MOTION: readonly [number, number, number, number] = [
  0.7, 0, 0.84, 0,
] as const;

/** For position changes — elements moving between anchor points. */
export const EASE_POSITION_CSS = 'cubic-bezier(0.87, 0, 0.13, 1)' as const;
export const EASE_POSITION_MOTION: readonly [number, number, number, number] = [
  0.87, 0, 0.13, 1,
] as const;

/** Standard durations (ms) paired with the curves above. */
export const DURATION_ENTRANCE_MS = 200 as const;
export const DURATION_EXIT_MS = 150 as const;
export const DURATION_HOVER_MS = 150 as const;
export const DURATION_TAP_MS = 100 as const;
export const DURATION_POSITION_MS = 300 as const;
/**
 * Position change for SMALL UI elements (switch thumbs, toggle indicators,
 * checkbox marks) where the travel distance is only a handful of pixels and
 * 300 ms feels sluggish. Same position curve as MOTION_POSITION so the
 * "moving A → B" semantic is preserved — just half the duration so micro-
 * interactions feel responsive.
 *
 * Picking guidance:
 *   - Travel ≲ ~20 px or element size ≲ ~32 px → MOTION_POSITION_SMALL
 *   - `SegmentedControl` sliding pill (all variants) → MOTION_POSITION_SMALL
 *   - Larger reorder / shared layout transitions  → MOTION_POSITION
 */
export const DURATION_POSITION_SMALL_MS = 150 as const;

/**
 * Pre-built Motion transition objects — use these on `<motion.div transition={…}>`.
 *
 * Eliminates the `/ 1000` ms→s conversion and enforces the correct curve+duration
 * pairing from the blueprint.
 *
 * ```tsx
 * import { MOTION_ENTRANCE, MOTION_EXIT, MOTION_HOVER, MOTION_TAP, MOTION_POSITION } from '@/lib/ui/easing';
 *
 * <motion.div
 *   initial={{ opacity: 0, y: 12 }}
 *   animate={{ opacity: 1, y: 0 }}
 *   transition={MOTION_ENTRANCE}
 * />
 *
 * <m.div whileHover={{ scale: 1.015 }} whileTap={{ scale: 0.98 }} transition={MOTION_HOVER} />
 *
 * <AnimatePresence>
 *   {show && (
 *     <motion.div exit={{ opacity: 0 }} transition={MOTION_EXIT} />
 *   )}
 * </AnimatePresence>
 *
 * <motion.li layout transition={MOTION_POSITION} />
 * ```
 */
export const MOTION_ENTRANCE = {
  duration: DURATION_ENTRANCE_MS / 1000,
  ease: EASE_DEFAULT_MOTION,
} as const;

export const MOTION_EXIT = {
  duration: DURATION_EXIT_MS / 1000,
  ease: EASE_EXIT_MOTION,
} as const;

export const MOTION_HOVER = {
  duration: DURATION_HOVER_MS / 1000,
  ease: EASE_DEFAULT_MOTION,
} as const;

export const MOTION_TAP = {
  duration: DURATION_TAP_MS / 1000,
  ease: EASE_DEFAULT_MOTION,
} as const;

export const MOTION_POSITION = {
  duration: DURATION_POSITION_MS / 1000,
  ease: EASE_POSITION_MOTION,
} as const;

/**
 * Faster sibling of MOTION_POSITION for small UI primitives. Same curve, half
 * the duration. Use on switch thumbs, toggle indicators, checkbox marks, and
 * any element whose travel distance is roughly its own size or smaller.
 *
 * ```tsx
 * <m.span animate={{ x: checked ? 'calc(100% - 2px)' : 0 }} transition={MOTION_POSITION_SMALL} />
 * ```
 */
export const MOTION_POSITION_SMALL = {
  duration: DURATION_POSITION_SMALL_MS / 1000,
  ease: EASE_POSITION_MOTION,
} as const;

/** `AppTabs` line variant — shared-layout underline; position curve at 200 ms. */
export const MOTION_TAB_LINE_UNDERLINE = {
  duration: DURATION_ENTRANCE_MS / 1000,
  ease: EASE_POSITION_MOTION,
} as const;
